/**
 * User dropdown menu component.
 *
 * Displays user info and provides logout action.
 * Two variants:
 * - sidebar: Shows avatar + display name (or just avatar when collapsed)
 * - topbar: Shows round button with first letter of display name
 */

import * as React from 'react';

import { Menu } from '@base-ui/react/menu';

import type { User } from '@/api/auth';
import { cn } from '@/lib/cn';
import { useAuthStore } from '@/store/auth';

export interface UserDropdownProps {
  /** Display variant */
  variant: 'sidebar' | 'topbar';
  /** Whether sidebar is collapsed (only applies to sidebar variant) */
  collapsed?: boolean;
}

/**
 * User dropdown with logout functionality.
 *
 * @example
 * // Sidebar variant
 * <UserDropdown variant="sidebar" collapsed={false} />
 *
 * // Topbar variant
 * <UserDropdown variant="topbar" />
 */
export function UserDropdown({ variant, collapsed = false }: UserDropdownProps) {
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);

  // Get first letter for avatar
  const initial = user?.display_name.charAt(0).toUpperCase() ?? '?';

  const handleLogout = React.useCallback(async () => {
    await logout();
  }, [logout]);

  if (variant === 'topbar') {
    return (
      <Menu.Root>
        <Menu.Trigger
          className={cn(
            'flex size-8 items-center justify-center rounded-full',
            'bg-muted text-sm font-medium text-muted-foreground',
            'hover:bg-muted/80',
            'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
            ' duration-150',
          )}
          aria-label="Меню пользователя"
        >
          {initial}
        </Menu.Trigger>
        <UserMenuPopup user={user} onLogout={handleLogout} />
      </Menu.Root>
    );
  }

  // Sidebar variant
  return (
    <Menu.Root>
      <Menu.Trigger
        className={cn(
          'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-1.5 font-mono',
          'text-left',
          'hover:bg-muted/70',
          'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
          ' duration-150',
          collapsed && 'justify-center px-1.5',
        )}
        aria-label="Меню пользователя"
      >
        <div
          className="flex size-7 flex-shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground"
          aria-hidden="true"
        >
          {initial}
        </div>
        {collapsed ? null : (
          <span className="truncate text-sm font-medium tracking-[0.01em] text-muted-foreground">
            {user?.display_name ?? 'Пользователь'}
          </span>
        )}
      </Menu.Trigger>
      <UserMenuPopup user={user} onLogout={handleLogout} />
    </Menu.Root>
  );
}

/**
 * Shared menu popup content.
 */
interface UserMenuPopupProps {
  user: User | null;
  onLogout: () => void;
}

function UserMenuPopup({ user, onLogout }: UserMenuPopupProps) {
  return (
    <Menu.Portal>
      <Menu.Positioner sideOffset={4} align="end" className="z-dropdown">
        <Menu.Popup
          className={cn(
            'min-w-[12rem] rounded-md border border-border bg-background py-1 shadow-md',
            'outline-none',
            // Transitions
            ' duration-150',
            'data-[ending-style]:scale-95 data-[ending-style]:opacity-0',
            'data-[starting-style]:scale-95 data-[starting-style]:opacity-0',
          )}
        >
          {/* User info header */}
          <div className="px-3 py-2">
            <div className="text-sm font-medium text-foreground">
              {user?.display_name ?? 'Пользователь'}
            </div>
            <div className="text-xs text-muted-foreground">{user?.role_label ?? 'Роль'}</div>
          </div>

          {/* Separator */}
          <Menu.Separator className="my-1 h-px bg-border" />

          {/* Logout button */}
          <Menu.Item
            className={cn(
              'flex w-full cursor-pointer items-center px-3 py-2 text-sm text-foreground',
              'outline-none',
              'data-[highlighted]:bg-muted',
            )}
            onClick={onLogout}
          >
            Выйти
          </Menu.Item>
        </Menu.Popup>
      </Menu.Positioner>
    </Menu.Portal>
  );
}
